import type { SxProps, Theme } from '@mui/material/styles';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Checkbox } from 'ui/checkbox';
import { Radio } from 'ui/radio';
import { RadioGroup } from 'ui/radio-group';
import { Switch } from 'ui/switch';

import CurrencyField from './currency-field';

import {
  AidModelCalculationRequestCalculationGiftTargetType,
  AidModelCalculationRequestGrantCap,
} from 'api/generated/schemas';

import {
  GIFT_TARGET_OPTIONS,
  GRANT_CAP_OPTIONS,
} from '../calculations-step.constants';

import type {
  CalculationGiftTargetType,
  CalculationGrantCap,
  CalculationSectionVisibility,
  CalculationSettingsDraft,
} from '../calculations-step.types';

interface SettingsOption<TValue> {
  value: TValue;
  label: string;
}

export interface CalculationSettingsPanelProps {
  settings: CalculationSettingsDraft;
  visibility: CalculationSectionVisibility;
  onGiftTargetChange: (value: CalculationGiftTargetType) => void;
  onIncludeRequiredFeesChange: (value: boolean) => void;
  onAdjustmentLoansChange: (value: number | null) => void;
  onAdjustmentWorkStudyChange: (value: number | null) => void;
  onCalculateNewNeedChange: (value: boolean) => void;
  onGrantCapChange: (value: CalculationGrantCap) => void;
  onGrantCapAmountChange: (value: number | null) => void;
  giftTargetOptions?: ReadonlyArray<SettingsOption<CalculationGiftTargetType>>;
  grantCapOptions?: ReadonlyArray<SettingsOption<CalculationGrantCap>>;
  disabledGiftTargets?: ReadonlySet<CalculationGiftTargetType>;
  grantCapAmountError?: boolean;
  disabled?: boolean;
}

const LEGEND_SX: SxProps<Theme> = {
  mb: 1.5,
  fontWeight: 600,
  color: 'tokens.gray.700',
};

const CalculationSettingsPanel = ({
  settings,
  visibility,
  onGiftTargetChange,
  onIncludeRequiredFeesChange,
  onAdjustmentLoansChange,
  onAdjustmentWorkStudyChange,
  onCalculateNewNeedChange,
  onGrantCapChange,
  onGrantCapAmountChange,
  giftTargetOptions = GIFT_TARGET_OPTIONS,
  grantCapOptions = GRANT_CAP_OPTIONS,
  disabledGiftTargets,
  grantCapAmountError,
  disabled = false,
}: CalculationSettingsPanelProps) => {
  const showIncludeRequiredFees =
    visibility.includeRequiredFees &&
    settings.calculationGiftTargetType ===
      AidModelCalculationRequestCalculationGiftTargetType.TUITION;

  const showGrantCapAmount =
    settings.grantCap !== AidModelCalculationRequestGrantCap.NO_GRANT_CAP;

  return (
    <Stack data-testid="calculation-settings-panel" sx={{ gap: 4 }}>
      {visibility.giftTarget && (
        <Box>
          <Box id="gift-target-label" sx={LEGEND_SX}>
            Calculate gift target using
          </Box>
          <RadioGroup<CalculationGiftTargetType>
            aria-labelledby="gift-target-label"
            row
            size="small"
            value={settings.calculationGiftTargetType}
            onChange={(_event, value) => onGiftTargetChange(value)}
            disabled={disabled}
            sx={{ mb: 1.5 }}
          >
            {giftTargetOptions.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                label={option.label}
                id={`gift-target-${option.value}`}
                disabled={disabled || !!disabledGiftTargets?.has(option.value)}
              />
            ))}
          </RadioGroup>
        </Box>
      )}

      {showIncludeRequiredFees && (
        <Switch
          size="small"
          label="Include required fees in target calculation"
          checked={settings.includeRequiredFees}
          onChange={(_event, checked) => onIncludeRequiredFeesChange(checked)}
          disabled={disabled}
          data-testid="include-required-fees-toggle"
        />
      )}

      {visibility.loansWorkStudy && (
        <fieldset
          className="m-0 border-0 p-0"
          data-testid="loans-work-study-section"
        >
          <legend className="font-semibold text-gray-700">
            Adjustments to target
          </legend>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              '@media (min-width: 1024px)': { flexDirection: 'row', gap: 4 },
            }}
          >
            <Box
              sx={{
                width: '100%',
                '@media (min-width: 1024px)': { width: '16rem' },
              }}
            >
              <CurrencyField
                label="Loans"
                labelFor="adjustment-loans"
                value={settings.adjustmentLoans}
                onChange={onAdjustmentLoansChange}
                disabled={disabled}
                data-testid="adjustment-loans"
                size="medium"
              />
            </Box>
            <Box
              sx={{
                width: '100%',
                '@media (min-width: 1024px)': { width: '16rem' },
              }}
            >
              <CurrencyField
                label="Work Study"
                labelFor="adjustment-work-study"
                value={settings.adjustmentWorkStudy}
                onChange={onAdjustmentWorkStudyChange}
                disabled={disabled}
                data-testid="adjustment-work-study"
                size="medium"
              />
            </Box>
          </Box>
        </fieldset>
      )}

      {visibility.calculateNewNeed && (
        <Checkbox
          id="calculate-new-need"
          label="Use recalculated Need to determine Need Ranks"
          checked={settings.calculateNewNeed}
          onChange={() => onCalculateNewNeedChange(!settings.calculateNewNeed)}
          disabled={disabled}
          size="small"
          data-testid="checkbox-calculate-new-need"
        />
      )}

      {visibility.grantCap && (
        <Box>
          <Box id="grant-cap-label" sx={LEGEND_SX}>
            Grant cap
          </Box>
          <RadioGroup<CalculationGrantCap>
            aria-labelledby="grant-cap-label"
            row
            size="small"
            value={settings.grantCap}
            onChange={(_event, value) => onGrantCapChange(value)}
            disabled={disabled}
            sx={{ mb: 1.5 }}
          >
            {grantCapOptions.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                label={option.label}
                id={`grant-cap-${option.value}`}
              />
            ))}
          </RadioGroup>

          {showGrantCapAmount && (
            <Box
              sx={{
                mt: 1.5,
                width: '100%',
                '@media (min-width: 1024px)': { width: '16rem' },
              }}
            >
              <CurrencyField
                label="Grant cap amount"
                labelFor="grant-cap-amount"
                value={settings.grantCapAmount}
                onChange={onGrantCapAmountChange}
                required
                disabled={disabled}
                error={
                  !!grantCapAmountError && settings.grantCapAmount == null
                }
                data-testid="grant-cap-amount"
                size="medium"
              />
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default CalculationSettingsPanel;
